/**
 * IMPORTANT
 *
 * If you want to add a new locale, please do the following things
 *
 * 1) Add the locale string to the config.matcher regex in the middleware.ts file as it does not support dynamic values
 *
 * 2) Add the locale string to the Locale enum
 *
 * 3) Add a new file with all translations in the messages directory with the name of the locale string and the .json extension
 *
 * 4) Add locale-specific routes to the LocalePathnameConfig (see below)
 *
 * 5) Test the page extensively to make sure that everything works as expected
 */
import { run } from '@/utils/run'

export enum Locale {
    EN_GB = 'en-GB',
    EN_DE = 'en-DE',
    EN_NL = 'en-NL',
    EN_AT = 'en-AT',

    DE_DE = 'de-DE',
    DE_AT = 'de-AT',

    NL_NL = 'nl-NL',
}

export enum Region {
    DE = 'DE',
    AT = 'AT',
    GB = 'GB',
    NL = 'NL',
}

export enum Language {
    DE = 'de',
    EN = 'en',
    NL = 'nl',
}

export enum Currency {
    EUR = 'EUR',
    USD = 'USD',
    GBP = 'GBP',
}

export const REGION_LANGUAGE_LOCALE_MAP: Record<
    Region,
    { defaultLocale: Locale; languageToLocaleMap: Record<Language, Locale | null> }
> = {
    [Region.DE]: {
        defaultLocale: Locale.DE_DE,
        languageToLocaleMap: {
            [Language.DE]: Locale.DE_DE,
            [Language.EN]: Locale.EN_DE,
            [Language.NL]: null,
        },
    },
    [Region.AT]: {
        defaultLocale: Locale.DE_AT,
        languageToLocaleMap: {
            [Language.DE]: Locale.DE_AT,
            [Language.EN]: Locale.EN_AT,
            [Language.NL]: null,
        },
    },
    [Region.GB]: {
        defaultLocale: Locale.EN_GB,
        languageToLocaleMap: {
            [Language.DE]: null,
            [Language.EN]: Locale.EN_GB,
            [Language.NL]: null,
        },
    },
    [Region.NL]: {
        defaultLocale: Locale.NL_NL,
        languageToLocaleMap: {
            [Language.DE]: null,
            [Language.EN]: Locale.EN_NL,
            [Language.NL]: Locale.NL_NL,
        },
    },
}

export function getLanguagesForRegion(region: Region) {
    return Object.values(Language).filter(
        (language) => REGION_LANGUAGE_LOCALE_MAP[region].languageToLocaleMap[language]
    )
}

export function getLocaleForRegion(nextRegion: Region, currentLanguage: Language): Locale {
    return (
        REGION_LANGUAGE_LOCALE_MAP[nextRegion].languageToLocaleMap[currentLanguage] ??
        REGION_LANGUAGE_LOCALE_MAP[nextRegion].defaultLocale
    )
}

export const GOOGLE_PLACES_COUNTRY_CODES = [
    ...Object.values(Locale).map((locale): string => {
        switch (locale) {
            case Locale.DE_DE:
            case Locale.EN_DE:
                return 'de'

            case Locale.DE_AT:
            case Locale.EN_AT:
                return 'at'

            case Locale.EN_GB:
                return 'gb'

            case Locale.NL_NL:
            case Locale.EN_NL:
                return 'nl'
        }
    }),
    'at',
]

export function getFullCountryLocale(locale: Locale | string): `${string}_${string}` {
    switch (locale as Locale) {
        case Locale.DE_DE:
            return 'de_DE'

        case Locale.EN_DE:
            return 'en_DE'

        case Locale.EN_AT:
            return 'en_AT'

        case Locale.DE_AT:
            return 'de_AT'

        case Locale.EN_GB:
            return 'en_GB'

        case Locale.EN_NL:
            return 'en_NL'

        case Locale.NL_NL:
            return 'nl_NL'
    }
}

export function toLocalePriceString({
    price,
    locale,
    currencySymbol,
    rounded = false,
}: {
    price: number
    locale: Locale | string
    currencySymbol?: string
    rounded?: boolean
}) {
    return price.toLocaleString(locale, {
        style: 'currency',
        currencyDisplay: 'symbol',
        currency: run((): Currency => {
            switch (currencySymbol) {
                case '€':
                    return Currency.EUR
                case '$':
                    return Currency.USD
                case '£':
                    return Currency.GBP
                default:
                    return parseCurrencyFromLocale(locale) ?? Currency.EUR
            }
        }),
        minimumFractionDigits: rounded ? 0 : 2,
        maximumFractionDigits: rounded ? 0 : 2,
    })
}

export function parseRegionFromLocale(locale: Locale | string): Region {
    const rawRegion = new Intl.Locale(locale).region

    if (Object.values(Region).includes(rawRegion as Region)) {
        return rawRegion as Region
    }

    return Region.DE
}

export function parseLanguageFromLocale(locale: Locale | string): Language {
    const rawLanguage = new Intl.Locale(locale).language

    if (Object.values(Language).includes(rawLanguage as Language)) {
        return rawLanguage as Language
    }

    return Language.EN
}

export function parseCurrencyFromLocale(locale: Locale | string): Currency {
    switch (parseRegionFromLocale(locale)) {
        case Region.DE:
        case Region.AT:
            return Currency.EUR

        case Region.GB:
            return Currency.GBP

        case Region.NL:
            return Currency.EUR
    }
}
